import { FC, useState } from "react";
import { makeSxStyles } from "ui";
import { Box } from "@mui/material";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { DriverMarker as DriverMarkerType } from "./map/map.types";
import { DriverStartingPointTooltip } from "../pages/dashboard/route-planner/driver-starting-point-details";

const hasValidCoordinates = (
  latitude?: number,
  longitude?: number,
): latitude is number =>
  Number.isFinite(latitude) && Number.isFinite(longitude);

const DriverMarker: FC<DriverMarkerType> = ({
  startingPoint,
  startTime,
  actualStartTime,
  fullName,
  liveLocation,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (event: React.MouseEvent) => {
    setHoverPosition({ x: event.clientX, y: event.clientY });
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const styles = useStyles();

  const hasStartingPoint = hasValidCoordinates(
    startingPoint?.lat,
    startingPoint?.lng,
  );
  const hasLiveLocation = hasValidCoordinates(
    liveLocation?.latitude,
    liveLocation?.longitude,
  );

  if (!hasStartingPoint && !hasLiveLocation) return null;

  const driverPosition = {
    lat: hasLiveLocation ? liveLocation.latitude : startingPoint!.lat,
    lng: hasLiveLocation ? liveLocation.longitude : startingPoint!.lng,
  };

  return (
    <Box sx={styles.driverMarkerWrapper}>
      <AdvancedMarker
        clickable={false}
        position={driverPosition}
        onClick={() => {
          void 0;
        }}
      >
        <Box sx={styles.marker} data-testid="driver-marker-background">
          <Box
            sx={styles.dot}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        </Box>
      </AdvancedMarker>
      {startingPoint && (
        <DriverStartingPointTooltip
          isHovering={isHovering}
          position={hoverPosition}
          details={startingPoint}
          driverFullName={fullName}
          startTime={startTime}
          actualStartTime={actualStartTime}
        />
      )}
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  marker: {
    position: "relative",
    width: "40px",
    height: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "9999px",
    background: "rgba(20, 18, 24, 0.16)",
  },
  dot: {
    position: "absolute",
    width: "8px",
    height: "8px",
    borderRadius: "9999px",
    backgroundColor: "#000000",
    zIndex: 1,
  },
  driverMarkerWrapper: {
    display: "flex",
    position: "relative",
  },
}));

export default DriverMarker;
